import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useClientStore } from '../../store/useStore';
import RestaurantMap from '../../components/maps/RestaurantMap';
import {
  Calendar, Clock, Users, MapPin, Bell, Plus, Search, LogOut, Gift,
  Share2, Navigation, X, User, Phone, MessageCircle, Utensils,
  ChevronRight, MessageSquare, Ticket, PartyPopper, Check,
  Upload, LocateFixed, Edit3, XCircle, RefreshCw, QrCode,
  Download, Mail, Sparkles, Star, Trophy, Home, CalendarDays,
  CreditCard, Settings, ChevronDown, Award, Zap, Heart, TrendingUp,
  Shield, KeyRound, ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getToken = () => localStorage.getItem('client_token');

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${getToken()}`
});

// ==================== STATUS HELPERS ====================
const STATUS_COLOR = {
  pending:   'bg-amber-100 text-amber-700 border-amber-200',
  confirmed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  seated:    'bg-blue-100 text-blue-700 border-blue-200',
  completed: 'bg-slate-100 text-slate-600 border-slate-200',
  cancelled: 'bg-red-100 text-red-600 border-red-200',
  no_show:   'bg-red-100 text-red-600 border-red-200',
};
const STATUS_TEXT = {
  pending: 'Pendiente', confirmed: 'Confirmada', seated: 'En mesa',
  completed: 'Concretada', cancelled: 'Cancelada', no_show: 'No asistió',
};
const formatDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString('es-MX', {
  weekday: 'short', day: 'numeric', month: 'short'
});

// Tier config for loyalty
const TIER_CONFIG = {
  Bronce:   { color: '#CD7F32', bg: 'from-amber-800 to-amber-600',   icon: '🥉', min: 0    },
  Plata:    { color: '#94A3B8', bg: 'from-slate-500 to-slate-400',   icon: '🥈', min: 500  },
  Oro:      { color: '#F59E0B', bg: 'from-yellow-600 to-yellow-400', icon: '🥇', min: 1500 },
  Diamante: { color: '#60A5FA', bg: 'from-blue-600 to-blue-400',     icon: '💎', min: 5000 },
};
const getTier = (pts = 0) => {
  if (pts >= 5000) return 'Diamante';
  if (pts >= 1500) return 'Oro';
  if (pts >= 500)  return 'Plata';
  return 'Bronce';
};
const getNextTierMin = (tier) => {
  const next = { Bronce: 500, Plata: 1500, Oro: 5000, Diamante: null };
  return next[tier];
};

// ==================== MAIN DASHBOARD ====================
export default function ClientDashboard() {
  const { nickname } = useParams();
  const navigate = useNavigate();
  const { client, clientToken, isClientAuthenticated, requiresPinChange, clientLogout, setClient } = useClientStore();

  const [tab, setTab]                     = useState('home');
  const [reservations, setReservations]   = useState([]);
  const [loyaltyData, setLoyaltyData]     = useState(null);
  const [notifications, setNotifications] = useState({ notifications: [], unread_count: 0 });
  const [loading, setLoading]             = useState(true);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Modal state
  const [showNotifications, setShowNotifications]     = useState(false);
  const [showNewReservation, setShowNewReservation]   = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showShareModal, setShowShareModal]           = useState(false);
  const [showGiftModal, setShowGiftModal]             = useState(false);
  const [showManageModal, setShowManageModal]         = useState(false);
  const [showDirectionsModal, setShowDirectionsModal] = useState(false);
  const [showMessageModal, setShowMessageModal]       = useState(false);
  const [showRebookModal, setShowRebookModal]         = useState(false);

  // Auth guard
  useEffect(() => {
    if (!isClientAuthenticated) navigate(`/clientes/${nickname || ''}`);
    else if (requiresPinChange && client && !client.pin_changed)
      navigate(`/clientes/${nickname}/cambiar-pin`);
  }, [isClientAuthenticated, requiresPinChange, navigate, nickname, client]);

  const token = clientToken || getToken();

  const fetchAll = useCallback(async () => {
    if (!isClientAuthenticated) return;
    setLoading(true);
    try {
      const [resRes, notifRes, loyaltyRes] = await Promise.allSettled([
        fetch(`${API_URL}/api/clients/my/reservations`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/clients/my/notifications`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/clients/my/loyalty`, { headers: authHeaders() }),
      ]);
      if (resRes.status === 'fulfilled' && resRes.value.ok) {
        const d = await resRes.value.json();
        setReservations(Array.isArray(d) ? d : (d.reservations || d.items || []));
      }
      if (notifRes.status === 'fulfilled' && notifRes.value.ok) {
        const d = await notifRes.value.json();
        if (d && typeof d === 'object' && 'notifications' in d) setNotifications(d);
      }
      if (loyaltyRes.status === 'fulfilled' && loyaltyRes.value.ok)
        setLoyaltyData(await loyaltyRes.value.json());
    } finally {
      setLoading(false);
    }
  }, [isClientAuthenticated]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleLogout = () => { clientLogout(); toast.success('Sesión cerrada'); navigate('/clientes'); };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingAvatar(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const res = await fetch(`${API_URL}/api/clients/my/avatar`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ avatar: reader.result })
        });
        if (res.ok) {
          toast.success('Foto actualizada');
          const updated = await res.json();
          if (updated.avatar_url) setClient({ ...client, avatar_url: updated.avatar_url });
        } else toast.error('Error al subir imagen');
      } catch { toast.error('Error al subir imagen'); }
      finally { setUploadingAvatar(false); }
    };
    reader.readAsDataURL(file);
  };

  const points  = loyaltyData?.points ?? client?.loyalty_points ?? 0;
  const tier    = getTier(points);
  const tierCfg = TIER_CONFIG[tier];
  const nextMin = getNextTierMin(tier);
  const prevMin = TIER_CONFIG[tier].min;
  const progress = nextMin ? Math.min(100, ((points - prevMin) / (nextMin - prevMin)) * 100) : 100;

  const upcoming = Array.isArray(reservations)
    ? reservations.filter(r => ['pending','confirmed','seated'].includes(r.status)).sort((a,b) => (a.date||'').localeCompare(b.date||''))
    : [];

  if (loading) return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-3 border-orange-500/30 border-t-orange-500 rounded-full animate-spin mx-auto mb-4" style={{ borderWidth: 3 }} />
        <p className="text-slate-400 text-sm">Cargando tu cuenta...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col w-full lg:max-w-none" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      {/* ===== HEADER ===== */}
      <header className="sticky top-0 z-40 bg-slate-900 shadow-xl">
        <div className="px-4 pt-4 pb-3 flex items-center justify-between">
          {/* Avatar + greeting */}
          <div className="flex items-center gap-3">
            <label className="relative cursor-pointer group">
              <div className="w-11 h-11 rounded-2xl overflow-hidden ring-2 ring-orange-500/50 bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                {client?.avatar_url
                  ? <img src={client.avatar_url} alt="" className="w-full h-full object-cover" />
                  : <span className="text-white font-bold text-lg">{client?.name?.charAt(0).toUpperCase()}</span>
                }
              </div>
              {uploadingAvatar && (
                <div className="absolute inset-0 bg-black/60 rounded-2xl flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                </div>
              )}
              <div className="absolute inset-0 bg-black/40 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Upload className="w-4 h-4 text-white" />
              </div>
              <input type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} disabled={uploadingAvatar} />
            </label>
            <div>
              <p className="text-slate-400 text-xs">Bienvenido</p>
              <h1 className="text-white font-bold text-base leading-tight">{client?.name?.split(' ')[0]}</h1>
            </div>
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            {/* Tier badge */}
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r ${tierCfg.bg} shadow-sm`}>
              <span className="text-sm leading-none">{tierCfg.icon}</span>
              <span className="text-white text-xs font-semibold">{points.toLocaleString()} pts</span>
            </div>
            {/* Bell */}
            <button
              onClick={() => setShowNotifications(true)}
              className="relative w-10 h-10 rounded-xl bg-slate-800 hover:bg-slate-700 flex items-center justify-center transition-colors"
            >
              <Bell className="w-5 h-5 text-slate-400" />
              {notifications.unread_count > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs text-white flex items-center justify-center font-bold">
                  {notifications.unread_count > 9 ? '9+' : notifications.unread_count}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Tab indicators */}
        <div className="flex border-t border-slate-800">
          {[
            { id: 'home',    label: 'Inicio',    icon: Home },
            { id: 'reservas', label: 'Reservas',  icon: CalendarDays },
            { id: 'beneficios', label: 'Beneficios', icon: Star },
            { id: 'perfil',  label: 'Perfil',    icon: User },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 text-xs font-medium transition-all ${
                tab === id
                  ? 'text-orange-400 border-b-2 border-orange-500'
                  : 'text-slate-500 hover:text-slate-300'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>
      </header>

      {/* ===== CONTENT ===== */}
      <main className="flex-1 overflow-y-auto pb-4">

        {/* HOME TAB */}
        {tab === 'home' && (
          <div className="space-y-4 p-4">

            {/* Loyalty card */}
            <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-r ${tierCfg.bg} p-5 shadow-lg`}>
              <div className="absolute -right-6 -top-6 w-32 h-32 bg-white/10 rounded-full" />
              <div className="absolute -right-2 -bottom-8 w-24 h-24 bg-white/5 rounded-full" />
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-white/70 text-xs font-medium uppercase tracking-wider">Nivel {tier}</p>
                    <p className="text-white text-3xl font-black">{points.toLocaleString()}</p>
                    <p className="text-white/70 text-xs">puntos acumulados</p>
                  </div>
                  <div className="text-5xl">{tierCfg.icon}</div>
                </div>
                {nextMin && (
                  <>
                    <div className="h-2 bg-white/20 rounded-full overflow-hidden mb-1">
                      <div className="h-full bg-white rounded-full transition-all duration-700" style={{ width: `${progress}%` }} />
                    </div>
                    <p className="text-white/60 text-xs">{(nextMin - points).toLocaleString()} pts para {Object.keys(TIER_CONFIG).find(k => TIER_CONFIG[k].min === nextMin)}</p>
                  </>
                )}
                {!nextMin && <p className="text-white/80 text-sm font-semibold mt-1">¡Nivel máximo alcanzado! 🎉</p>}
              </div>
            </div>

            {/* Next reservation */}
            {upcoming.length > 0 ? (
              <div>
                <h2 className="text-slate-700 font-bold text-sm mb-2 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-orange-500" />
                  Próxima reservación
                </h2>
                <NextReservationCard
                  reservation={upcoming[0]}
                  onManage={() => { setSelectedReservation(upcoming[0]); setShowManageModal(true); }}
                  onDirections={() => { setSelectedReservation(upcoming[0]); setShowDirectionsModal(true); }}
                  onQR={() => { setSelectedReservation(upcoming[0]); }}
                />
              </div>
            ) : (
              <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm text-center">
                <div className="w-14 h-14 bg-orange-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <CalendarDays className="w-7 h-7 text-orange-400" />
                </div>
                <h3 className="font-bold text-slate-800 mb-1">Sin reservaciones próximas</h3>
                <p className="text-slate-400 text-sm mb-4">¡Haz tu primera reservación ahora!</p>
                <button
                  onClick={() => setShowNewReservation(true)}
                  className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-semibold text-sm hover:from-orange-600 hover:to-orange-700 transition-all shadow-md shadow-orange-500/30"
                >
                  Nueva Reservación
                </button>
              </div>
            )}

            {/* Quick actions */}
            <div>
              <h2 className="text-slate-700 font-bold text-sm mb-2 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-orange-500" />
                Acciones rápidas
              </h2>
              <div className="grid grid-cols-4 gap-3">
                {[
                  { label: 'Reservar', icon: Plus, color: 'from-orange-500 to-orange-600', onClick: () => setShowNewReservation(true) },
                  { label: 'Reservas', icon: CalendarDays, color: 'from-blue-500 to-blue-600', onClick: () => setTab('reservas') },
                  { label: 'Puntos', icon: Star, color: 'from-amber-500 to-amber-600', onClick: () => setTab('beneficios') },
                  { label: 'Perfil', icon: User, color: 'from-slate-600 to-slate-700', onClick: () => setTab('perfil') },
                ].map(({ label, icon: Icon, color, onClick }) => (
                  <button key={label} onClick={onClick}
                    className={`flex flex-col items-center gap-2 py-3 rounded-2xl bg-gradient-to-b ${color} text-white shadow-sm hover:shadow-md active:scale-95 transition-all`}>
                    <Icon className="w-5 h-5" />
                    <span className="text-xs font-semibold leading-none">{label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Visitas', value: client?.total_reservations ?? reservations.length, icon: Heart, color: 'text-rose-500', bg: 'bg-rose-50' },
                { label: 'Completadas', value: reservations.filter(r => r.status === 'completed').length, icon: Check, color: 'text-emerald-500', bg: 'bg-emerald-50' },
                { label: 'Puntos', value: points, icon: Trophy, color: 'text-amber-500', bg: 'bg-amber-50' },
              ].map(({ label, value, icon: Icon, color, bg }) => (
                <div key={label} className="bg-white rounded-2xl p-3 border border-slate-100 shadow-sm text-center">
                  <div className={`w-9 h-9 ${bg} rounded-xl flex items-center justify-center mx-auto mb-2`}>
                    <Icon className={`w-4 h-4 ${color}`} />
                  </div>
                  <p className="font-black text-slate-800 text-xl">{value.toLocaleString()}</p>
                  <p className="text-slate-400 text-xs">{label}</p>
                </div>
              ))}
            </div>

            {/* All upcoming reservations */}
            {upcoming.length > 1 && (
              <div>
                <h2 className="text-slate-700 font-bold text-sm mb-2">Otras próximas</h2>
                <div className="space-y-2">
                  {upcoming.slice(1, 4).map(r => (
                    <CompactReservationCard
                      key={r.id} reservation={r}
                      onTap={() => { setSelectedReservation(r); setShowManageModal(true); }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* RESERVAS TAB */}
        {tab === 'reservas' && (
          <ReservationsTab
            reservations={reservations}
            token={token}
            onRefresh={fetchAll}
            onShare={(r) => { setSelectedReservation(r); setShowShareModal(true); }}
            onGift={(r) => { setSelectedReservation(r); setShowGiftModal(true); }}
            onManage={(r) => { setSelectedReservation(r); setShowManageModal(true); }}
            onDirections={(r) => { setSelectedReservation(r); setShowDirectionsModal(true); }}
            onMessage={(r) => { setSelectedReservation(r); setShowMessageModal(true); }}
            onRebook={(r) => { setSelectedReservation(r); setShowRebookModal(true); }}
            onNewReservation={() => setShowNewReservation(true)}
          />
        )}

        {/* BENEFICIOS TAB */}
        {tab === 'beneficios' && (
          <BeneficiosTab token={token} points={points} tier={tier} tierCfg={tierCfg} nextMin={nextMin} progress={progress} />
        )}

        {/* PERFIL TAB */}
        {tab === 'perfil' && (
          <PerfilTab
            client={client}
            reservations={reservations}
            points={points}
            tier={tier}
            tierCfg={tierCfg}
            onLogout={handleLogout}
            onChangePin={() => navigate(`/clientes/${nickname}/cambiar-pin`)}
            onAvatarUpload={handleAvatarUpload}
            uploadingAvatar={uploadingAvatar}
          />
        )}
      </main>

      {/* FAB: Nueva reserva */}
      <button
        onClick={() => setShowNewReservation(true)}
        className="fixed bottom-6 right-4 w-14 h-14 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-xl shadow-orange-500/40 hover:shadow-orange-500/60 active:scale-95 transition-all z-30"
      >
        <Plus className="w-7 h-7 text-white" />
      </button>

      {/* ===== MODALS ===== */}
      <NotificationsPanel
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
        notifications={notifications.notifications}
        token={token}
        onRefresh={fetchAll}
      />
      <NewReservationMapModal
        isOpen={showNewReservation}
        onClose={() => setShowNewReservation(false)}
        token={token}
        onSuccess={() => { fetchAll(); setShowNewReservation(false); }}
      />
      <ShareGiftModal isOpen={showShareModal} onClose={() => setShowShareModal(false)} type="share"
        reservation={selectedReservation} token={token} onSuccess={() => { fetchAll(); setShowShareModal(false); }} />
      <ShareGiftModal isOpen={showGiftModal} onClose={() => setShowGiftModal(false)} type="gift"
        reservation={selectedReservation} token={token} onSuccess={() => { fetchAll(); setShowGiftModal(false); }} />
      <ManageReservationModal isOpen={showManageModal} onClose={() => setShowManageModal(false)}
        reservation={selectedReservation} token={token} onSuccess={() => { fetchAll(); setShowManageModal(false); }} />
      <DirectionsModal isOpen={showDirectionsModal} onClose={() => setShowDirectionsModal(false)} reservation={selectedReservation} />
      <DirectMessageModal isOpen={showMessageModal} onClose={() => setShowMessageModal(false)}
        reservation={selectedReservation} token={token} onSuccess={() => { toast.success('Mensaje enviado'); setShowMessageModal(false); }} />
      <RebookModal isOpen={showRebookModal} onClose={() => setShowRebookModal(false)}
        reservation={selectedReservation} token={token} onSuccess={() => { fetchAll(); setShowRebookModal(false); }} />
    </div>
  );
}

// ==================== NEXT RESERVATION CARD ====================
function NextReservationCard({ reservation, onManage, onDirections }) {
  const restaurant = reservation.restaurant || {};
  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm">
      {/* Top stripe */}
      <div className="h-1 bg-gradient-to-r from-orange-500 to-orange-400" />
      <div className="p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-14 h-14 rounded-xl overflow-hidden bg-orange-50 flex-shrink-0">
            {restaurant.logo_url
              ? <img src={restaurant.logo_url} alt="" className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center"><Utensils className="w-7 h-7 text-orange-400" /></div>
            }
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-slate-900 truncate">{restaurant.name || 'Restaurante'}</h3>
            <p className="text-slate-400 text-xs truncate">{restaurant.address}</p>
          </div>
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${STATUS_COLOR[reservation.status] || STATUS_COLOR.pending}`}>
            {STATUS_TEXT[reservation.status] || reservation.status}
          </span>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          {[
            { icon: Calendar, label: formatDate(reservation.date) },
            { icon: Clock,    label: reservation.time },
            { icon: Users,    label: `${reservation.party_size} pers.` },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="flex flex-col items-center gap-1.5 p-2.5 bg-slate-50 rounded-xl">
              <Icon className="w-4 h-4 text-orange-500" />
              <span className="text-slate-700 text-xs font-semibold">{label}</span>
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          <button onClick={onManage}
            className="flex-1 py-2.5 rounded-xl bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 flex items-center justify-center gap-1.5 transition-colors">
            <Edit3 className="w-4 h-4" /> Gestionar
          </button>
          <button onClick={onDirections}
            className="flex-1 py-2.5 rounded-xl bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-slate-200 flex items-center justify-center gap-1.5 transition-colors">
            <Navigation className="w-4 h-4" /> Cómo llegar
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================== COMPACT RESERVATION CARD ====================
function CompactReservationCard({ reservation, onTap }) {
  const restaurant = reservation.restaurant || {};
  return (
    <button onClick={onTap} className="w-full bg-white rounded-xl p-3 border border-slate-100 shadow-sm flex items-center gap-3 hover:shadow-md transition-shadow text-left">
      <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
        {restaurant.logo_url
          ? <img src={restaurant.logo_url} alt="" className="w-full h-full object-cover rounded-xl" />
          : <Utensils className="w-5 h-5 text-orange-400" />
        }
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-slate-800 text-sm truncate">{restaurant.name || 'Restaurante'}</p>
        <p className="text-slate-400 text-xs">{formatDate(reservation.date)} · {reservation.time} · {reservation.party_size} pers.</p>
      </div>
      <span className={`px-2 py-1 rounded-lg text-xs font-medium border ${STATUS_COLOR[reservation.status] || STATUS_COLOR.pending}`}>
        {STATUS_TEXT[reservation.status]}
      </span>
    </button>
  );
}

// ==================== RESERVATIONS TAB ====================
function ReservationsTab({ reservations, token, onRefresh, onShare, onGift, onManage, onDirections, onMessage, onRebook, onNewReservation }) {
  const [filter, setFilter] = useState('upcoming');

  const filtered = reservations.filter(r => {
    if (filter === 'upcoming') return ['pending','confirmed','seated'].includes(r.status);
    if (filter === 'past')     return ['completed','cancelled','no_show'].includes(r.status);
    return true;
  }).sort((a,b) => b.date.localeCompare(a.date));

  const handleCancel = async (r) => {
    if (!window.confirm('¿Cancelar esta reservación?')) return;
    try {
      const res = await fetch(`${API_URL}/api/clients/my/reservations/${r.id}/cancel`, {
        method: 'POST', headers: authHeaders()
      });
      if (res.ok) { toast.success('Reservación cancelada'); onRefresh(); }
      else throw new Error('Error al cancelar');
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="p-4 space-y-4">
      {/* Filter chips */}
      <div className="flex gap-2">
        {[
          { id: 'upcoming', label: 'Próximas' },
          { id: 'past',     label: 'Pasadas'  },
          { id: 'all',      label: 'Todas'    },
        ].map(({ id, label }) => (
          <button key={id} onClick={() => setFilter(id)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
              filter === id ? 'bg-orange-500 text-white shadow-sm' : 'bg-white text-slate-600 border border-slate-200 hover:border-orange-300'
            }`}>
            {label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl p-8 border border-slate-100 text-center shadow-sm">
          <CalendarDays className="w-14 h-14 text-slate-200 mx-auto mb-3" />
          <h3 className="font-bold text-slate-700 mb-1">Sin reservaciones</h3>
          <p className="text-slate-400 text-sm mb-4">
            {filter === 'upcoming' ? 'No tienes reservaciones próximas' : 'No hay reservaciones pasadas'}
          </p>
          <button onClick={onNewReservation}
            className="px-5 py-2 bg-orange-500 text-white rounded-xl font-semibold text-sm hover:bg-orange-600">
            Nueva Reservación
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(r => (
            <FullReservationCard
              key={r.id} reservation={r} token={token}
              onShare={() => onShare(r)} onGift={() => onGift(r)}
              onManage={() => onManage(r)} onDirections={() => onDirections(r)}
              onMessage={() => onMessage(r)} onCancel={() => handleCancel(r)}
              onRebook={() => onRebook(r)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== FULL RESERVATION CARD ====================
function FullReservationCard({ reservation, token, onShare, onGift, onManage, onDirections, onMessage, onCancel, onRebook }) {
  const restaurant = reservation.restaurant || {};
  const canModify  = ['pending','confirmed'].includes(reservation.status);
  const canRebook  = ['completed','cancelled'].includes(reservation.status);
  const [expanded, setExpanded] = useState(false);
  const [showQR, setShowQR]     = useState(false);
  const [qrCode, setQrCode]     = useState(null);
  const [loadingQR, setLoadingQR] = useState(false);

  const fetchQR = async () => {
    if (qrCode) { setShowQR(true); return; }
    setLoadingQR(true);
    try {
      const res = await fetch(`${API_URL}/api/clients/my/reservations/${reservation.id}/qr-code`, { headers: authHeaders() });
      if (res.ok) { const d = await res.json(); setQrCode(d.qr_code); setShowQR(true); }
      else toast.error('Error al obtener QR');
    } catch { toast.error('Error al obtener QR'); }
    finally { setLoadingQR(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      <div className="h-0.5 bg-gradient-to-r from-orange-500/60 to-transparent" />
      {/* Header */}
      <div className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-xl overflow-hidden bg-orange-50 flex-shrink-0">
            {restaurant.logo_url
              ? <img src={restaurant.logo_url} alt="" className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center"><Utensils className="w-6 h-6 text-orange-400" /></div>
            }
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-slate-900 truncate">{restaurant.name || 'Restaurante'}</h3>
            <p className="text-slate-400 text-xs truncate">{restaurant.address}</p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold border ${STATUS_COLOR[reservation.status] || STATUS_COLOR.pending}`}>
              {STATUS_TEXT[reservation.status]}
            </span>
            <button onClick={() => setExpanded(!expanded)} className="text-slate-400 hover:text-slate-600 transition-colors">
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>

        {/* Reservation info row */}
        <div className="flex gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-orange-400" />{formatDate(reservation.date)}</span>
          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-orange-400" />{reservation.time}</span>
          <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5 text-orange-400" />{reservation.party_size} pers.</span>
        </div>

        {/* Gifted badge */}
        {reservation.gifted_from && (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-purple-600 bg-purple-50 rounded-lg px-2.5 py-1.5">
            <Gift className="w-3.5 h-3.5" />
            <span>Regalada por {reservation.gifted_from.client_name}</span>
          </div>
        )}

        {/* Special requests */}
        {reservation.special_requests && (
          <p className="mt-2 text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2">{reservation.special_requests}</p>
        )}
      </div>

      {/* Expanded actions */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-50 pt-3 space-y-3">
          {/* QR for check-in */}
          {canModify && (
            showQR && qrCode ? (
              <div className="p-4 bg-orange-50 rounded-xl border border-orange-100">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
                    <QrCode className="w-4 h-4 text-orange-500" /> Código de Check-in
                  </h4>
                  <button onClick={() => setShowQR(false)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
                </div>
                <img src={qrCode} alt="QR" className="w-48 h-48 mx-auto rounded-xl bg-white p-2 shadow-sm" />
                <p className="text-xs text-center text-slate-500 mt-2 mb-3">Muestra este código al llegar</p>
                <div className="flex gap-2">
                  <button onClick={() => { const a = document.createElement('a'); a.download = `checkin.png`; a.href = qrCode; a.click(); toast.success('QR descargado'); }}
                    className="flex-1 py-2 text-xs font-medium bg-white border border-orange-200 text-orange-600 rounded-xl flex items-center justify-center gap-1">
                    <Download className="w-3.5 h-3.5" /> Descargar
                  </button>
                  <button onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Mi QR de check-in para ${restaurant.name || 'el restaurante'}`)}`, '_blank')}
                    className="flex-1 py-2 text-xs font-medium bg-green-500 text-white rounded-xl flex items-center justify-center gap-1">
                    <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={fetchQR} disabled={loadingQR}
                className="w-full py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 shadow-sm hover:shadow-md transition-all disabled:opacity-60">
                {loadingQR ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><QrCode className="w-4 h-4" /> Mi QR de Check-in</>}
              </button>
            )
          )}

          {/* Action buttons */}
          <div className="grid grid-cols-2 gap-2">
            <button onClick={onMessage} className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-blue-50 text-blue-600 text-sm font-medium hover:bg-blue-100 transition-colors">
              <MessageSquare className="w-4 h-4" /> Mensaje
            </button>
            <button onClick={onDirections} className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-slate-50 text-slate-700 text-sm font-medium hover:bg-slate-100 transition-colors">
              <Navigation className="w-4 h-4" /> Llegar
            </button>
            {canModify && <>
              <button onClick={onShare} className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-indigo-50 text-indigo-600 text-sm font-medium hover:bg-indigo-100 transition-colors">
                <Share2 className="w-4 h-4" /> Compartir
              </button>
              <button onClick={onGift} className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-purple-50 text-purple-600 text-sm font-medium hover:bg-purple-100 transition-colors">
                <Gift className="w-4 h-4" /> Regalar
              </button>
              <button onClick={onManage} className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-orange-50 text-orange-600 text-sm font-medium hover:bg-orange-100 transition-colors">
                <Edit3 className="w-4 h-4" /> Gestionar
              </button>
              <button onClick={onCancel} className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-red-50 text-red-600 text-sm font-medium hover:bg-red-100 transition-colors">
                <XCircle className="w-4 h-4" /> Cancelar
              </button>
            </>}
            {canRebook && (
              <button onClick={onRebook} className="col-span-2 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-emerald-50 text-emerald-600 text-sm font-semibold hover:bg-emerald-100 transition-colors">
                <RefreshCw className="w-4 h-4" /> Volver a reservar
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== BENEFICIOS TAB ====================
function BeneficiosTab({ token, points, tier, tierCfg, nextMin, progress }) {
  const [coupons, setCoupons] = useState([]);
  const [events,  setEvents]  = useState([]);
  const [giftCards, setGiftCards] = useState([]);
  const [membershipPlan, setMembershipPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('puntos');

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [c, e, g, m] = await Promise.allSettled([
        fetch(`${API_URL}/api/clients/my/coupons`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/clients/my/events`,  { headers: authHeaders() }),
        fetch(`${API_URL}/api/clients/my/gift-cards`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/clients/my/membership`, { headers: authHeaders() }),
      ]);
      if (c.status === 'fulfilled' && c.value.ok) setCoupons(await c.value.json());
      if (e.status === 'fulfilled' && e.value.ok) setEvents(await e.value.json());
      if (g.status === 'fulfilled' && g.value.ok) setGiftCards(await g.value.json());
      if (m.status === 'fulfilled' && m.value.ok) setMembershipPlan(await m.value.json());
      setLoading(false);
    })();
  }, []);

  const sections = [
    { id: 'puntos', label: 'Puntos', icon: Star, count: null },
    { id: 'cupones', label: 'Cupones', icon: Ticket, count: coupons.length },
    { id: 'eventos', label: 'Eventos', icon: PartyPopper, count: events.length },
    { id: 'tarjetas', label: 'Gift Cards', icon: CreditCard, count: giftCards.length },
  ];

  return (
    <div className="p-4 space-y-4">
      {/* Section tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
        {sections.map(({ id, label, icon: Icon, count }) => (
          <button key={id} onClick={() => setActiveSection(id)}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-all flex-shrink-0 ${
              activeSection === id ? 'bg-orange-500 text-white shadow-sm' : 'bg-white text-slate-600 border border-slate-200 hover:border-orange-300'
            }`}>
            <Icon className="w-3.5 h-3.5" />
            {label}
            {count !== null && count > 0 && (
              <span className={`ml-1 w-5 h-5 rounded-full text-xs flex items-center justify-center font-bold ${
                activeSection === id ? 'bg-white/30 text-white' : 'bg-orange-100 text-orange-600'
              }`}>{count}</span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><div className="w-8 h-8 border-2 border-orange-200 border-t-orange-500 rounded-full animate-spin" /></div>
      ) : (
        <>
          {/* PUNTOS */}
          {activeSection === 'puntos' && (
            <div className="space-y-4">
              {/* Big tier card */}
              <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${tierCfg.bg} p-6 text-white shadow-lg`}>
                <div className="absolute -right-8 -top-8 w-36 h-36 bg-white/10 rounded-full" />
                <div className="relative z-10 text-center">
                  <div className="text-5xl mb-2">{tierCfg.icon}</div>
                  <h2 className="text-3xl font-black">{points.toLocaleString()}</h2>
                  <p className="text-white/70 text-sm mb-4">puntos • Nivel {tier}</p>
                  {nextMin ? (
                    <>
                      <div className="h-3 bg-white/20 rounded-full overflow-hidden mb-2">
                        <div className="h-full bg-white rounded-full" style={{ width: `${progress}%` }} />
                      </div>
                      <p className="text-white/70 text-xs">Faltan {(nextMin - points).toLocaleString()} pts para el siguiente nivel</p>
                    </>
                  ) : <p className="text-white/80 font-semibold">¡Nivel máximo!</p>}
                </div>
              </div>

              {/* Membership badge */}
              {membershipPlan ? (
                <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-4 text-white shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <Award className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-white/70 text-xs">Membresía activa</p>
                      <h3 className="font-bold text-white">{membershipPlan.name}</h3>
                    </div>
                    <span className="ml-auto bg-white/20 text-white text-xs font-semibold px-3 py-1 rounded-full">Activa</span>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                      <Award className="w-5 h-5 text-slate-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-700">Sin membresía activa</p>
                      <p className="text-slate-400 text-xs">Consulta los planes disponibles</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Tier progression */}
              <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
                <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" /> Niveles de lealtad</h3>
                <div className="space-y-2">
                  {Object.entries(TIER_CONFIG).map(([name, cfg]) => (
                    <div key={name} className={`flex items-center gap-3 p-2.5 rounded-xl transition-all ${tier === name ? 'bg-orange-50 border border-orange-200' : ''}`}>
                      <span className="text-lg">{cfg.icon}</span>
                      <div className="flex-1">
                        <p className={`font-semibold text-sm ${tier === name ? 'text-orange-700' : 'text-slate-700'}`}>{name}</p>
                        <p className="text-slate-400 text-xs">Desde {cfg.min.toLocaleString()} puntos</p>
                      </div>
                      {tier === name && <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full font-semibold">Actual</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* CUPONES */}
          {activeSection === 'cupones' && (
            <div className="space-y-3">
              {coupons.length === 0 ? (
                <EmptyState icon={Ticket} title="Sin cupones" desc="No tienes cupones disponibles por ahora" />
              ) : coupons.map((c) => (
                <div key={c.id} className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Ticket className="w-6 h-6 text-orange-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-slate-800">{c.title}</h4>
                      <p className="text-slate-400 text-sm">{c.restaurant_name}</p>
                      {c.expires_at && <p className="text-xs text-slate-400 mt-0.5">Vence: {new Date(c.expires_at).toLocaleDateString('es-MX')}</p>}
                    </div>
                    <span className="px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-xl text-sm font-bold flex-shrink-0">{c.discount}% OFF</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* EVENTOS */}
          {activeSection === 'eventos' && (
            <div className="space-y-3">
              {events.length === 0 ? (
                <EmptyState icon={PartyPopper} title="Sin eventos" desc="No estás registrado en eventos" />
              ) : events.map((e) => (
                <div key={e.id} className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <PartyPopper className="w-6 h-6 text-purple-500" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-slate-800">{e.name}</h4>
                      <p className="text-slate-400 text-sm">{e.restaurant_name}</p>
                      <div className="flex gap-3 text-xs text-slate-400 mt-1">
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(e.date).toLocaleDateString('es-MX')}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{e.time}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* GIFT CARDS */}
          {activeSection === 'tarjetas' && (
            <div className="space-y-3">
              {giftCards.length === 0 ? (
                <EmptyState icon={CreditCard} title="Sin Gift Cards" desc="No tienes gift cards disponibles" />
              ) : giftCards.map((gc) => (
                <div key={gc.id} className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-2xl p-5 text-white shadow-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-slate-400 text-xs">Gift Card</p>
                      <p className="text-white font-bold text-xl">${(gc.balance_remaining || gc.amount || 0).toLocaleString()}</p>
                    </div>
                    <CreditCard className="w-8 h-8 text-orange-400" />
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-slate-400 text-xs font-mono">{gc.code}</p>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${gc.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600 text-slate-400'}`}>
                      {gc.status === 'active' ? 'Activa' : 'Usada'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ==================== PERFIL TAB ====================
function PerfilTab({ client, reservations, points, tier, tierCfg, onLogout, onChangePin, onAvatarUpload, uploadingAvatar }) {
  return (
    <div className="p-4 space-y-4">
      {/* Profile hero */}
      <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm text-center">
        <label className="relative cursor-pointer group inline-block mb-4">
          <div className="w-24 h-24 rounded-2xl overflow-hidden ring-4 ring-orange-100 bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center mx-auto">
            {client?.avatar_url
              ? <img src={client.avatar_url} alt="" className="w-full h-full object-cover" />
              : <span className="text-white font-black text-4xl">{client?.name?.charAt(0).toUpperCase()}</span>
            }
          </div>
          {uploadingAvatar && (
            <div className="absolute inset-0 bg-black/50 rounded-2xl flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            </div>
          )}
          <div className="absolute bottom-0 right-0 w-7 h-7 bg-orange-500 rounded-full flex items-center justify-center shadow-md group-hover:bg-orange-600 transition-colors">
            <Upload className="w-3.5 h-3.5 text-white" />
          </div>
          <input type="file" accept="image/*" className="hidden" onChange={onAvatarUpload} disabled={uploadingAvatar} />
        </label>
        <h2 className="font-black text-slate-900 text-2xl">{client?.name}</h2>
        <p className="text-orange-500 font-semibold">@{client?.nickname}</p>
        <div className={`inline-flex items-center gap-1.5 mt-2 px-4 py-1.5 rounded-full bg-gradient-to-r ${tierCfg.bg}`}>
          <span>{tierCfg.icon}</span>
          <span className="text-white text-xs font-bold">Nivel {tier}</span>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Reservas', value: reservations.length, icon: CalendarDays, color: 'text-blue-500', bg: 'bg-blue-50' },
          { label: 'Completadas', value: reservations.filter(r => r.status === 'completed').length, icon: Check, color: 'text-emerald-500', bg: 'bg-emerald-50' },
          { label: 'Puntos', value: points, icon: Star, color: 'text-amber-500', bg: 'bg-amber-50' },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className="bg-white rounded-2xl p-3 border border-slate-100 shadow-sm text-center">
            <div className={`w-9 h-9 ${bg} rounded-xl flex items-center justify-center mx-auto mb-2`}>
              <Icon className={`w-4 h-4 ${color}`} />
            </div>
            <p className="font-black text-slate-800 text-xl">{value.toLocaleString()}</p>
            <p className="text-slate-400 text-xs">{label}</p>
          </div>
        ))}
      </div>

      {/* Account info */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-50">
          <h3 className="font-bold text-slate-700 text-sm">Información de cuenta</h3>
        </div>
        {[
          { icon: Mail, label: 'Email', value: client?.email },
          { icon: Phone, label: 'Teléfono', value: client?.phone || 'No registrado' },
        ].map(({ icon: Icon, label, value }) => (
          <div key={label} className="flex items-center gap-3 px-4 py-3.5 border-b border-slate-50 last:border-0">
            <div className="w-9 h-9 bg-slate-50 rounded-xl flex items-center justify-center">
              <Icon className="w-4 h-4 text-slate-400" />
            </div>
            <div>
              <p className="text-xs text-slate-400">{label}</p>
              <p className="text-slate-700 font-medium text-sm">{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Settings actions */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-50">
          <h3 className="font-bold text-slate-700 text-sm">Configuración</h3>
        </div>
        <button onClick={onChangePin}
          className="w-full flex items-center gap-3 px-4 py-3.5 border-b border-slate-50 hover:bg-slate-50 transition-colors">
          <div className="w-9 h-9 bg-indigo-50 rounded-xl flex items-center justify-center">
            <KeyRound className="w-4 h-4 text-indigo-500" />
          </div>
          <div className="flex-1 text-left">
            <p className="text-slate-700 font-medium text-sm">Cambiar PIN</p>
            <p className="text-slate-400 text-xs">Actualiza tu código de acceso</p>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-300" />
        </button>
        <button onClick={onLogout}
          className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-red-50 transition-colors group">
          <div className="w-9 h-9 bg-red-50 rounded-xl flex items-center justify-center group-hover:bg-red-100 transition-colors">
            <LogOut className="w-4 h-4 text-red-500" />
          </div>
          <div className="flex-1 text-left">
            <p className="text-red-600 font-medium text-sm">Cerrar sesión</p>
            <p className="text-slate-400 text-xs">Salir de tu cuenta</p>
          </div>
        </button>
      </div>

      <p className="text-center text-slate-400 text-xs py-2">Powered by <span className="text-orange-500 font-semibold">OkReserva</span></p>
    </div>
  );
}

// ==================== EMPTY STATE ====================
function EmptyState({ icon: Icon, title, desc }) {
  return (
    <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm text-center">
      <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
        <Icon className="w-7 h-7 text-slate-300" />
      </div>
      <h3 className="font-bold text-slate-700 mb-1">{title}</h3>
      <p className="text-slate-400 text-sm">{desc}</p>
    </div>
  );
}

// ==================== NOTIFICATIONS PANEL ====================
function NotificationsPanel({ isOpen, onClose, notifications, token, onRefresh }) {
  const markAsRead = async (id) => {
    try {
      await fetch(`${API_URL}/api/clients/my/notifications/${id}/read`, { method: 'POST', headers: authHeaders() });
      onRefresh();
    } catch {}
  };
  const markAllAsRead = async () => {
    try {
      await fetch(`${API_URL}/api/clients/my/notifications/read-all`, { method: 'POST', headers: authHeaders() });
      onRefresh(); toast.success('Todas leídas');
    } catch {}
  };
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm">
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-white shadow-2xl flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <h2 className="font-bold text-slate-900">Notificaciones</h2>
          <div className="flex gap-2">
            {notifications.length > 0 && (
              <button onClick={markAllAsRead} className="text-sm text-orange-500 font-medium hover:text-orange-600">Marcar todas</button>
            )}
            <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-slate-50">
          {notifications.length === 0 ? (
            <div className="text-center py-14">
              <Bell className="w-12 h-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-400">Sin notificaciones</p>
            </div>
          ) : notifications.map((n) => (
            <div key={n.id} onClick={() => !n.read && markAsRead(n.id)}
              className={`p-4 cursor-pointer hover:bg-orange-50 transition-colors ${!n.read ? 'bg-orange-50/50' : ''}`}>
              <div className="flex gap-3">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${n.type === 'reservation_gifted' ? 'bg-purple-100' : n.type === 'reservation_shared' ? 'bg-blue-100' : 'bg-orange-100'}`}>
                  {n.type === 'reservation_gifted' ? <Gift className="w-4 h-4 text-purple-500" /> :
                   n.type === 'reservation_shared' ? <Share2 className="w-4 h-4 text-blue-500" /> :
                   <Bell className="w-4 h-4 text-orange-500" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800 text-sm">{n.title}</p>
                  <p className="text-slate-500 text-sm line-clamp-2">{n.message}</p>
                  <p className="text-slate-400 text-xs mt-1">{new Date(n.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                </div>
                {!n.read && <div className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0 mt-1.5" />}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ==================== NEW RESERVATION MAP MODAL ====================
function NewReservationMapModal({ isOpen, onClose, token, onSuccess }) {
  const [restaurants, setRestaurants]         = useState([]);
  const [search, setSearch]                   = useState('');
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [userLocation, setUserLocation]       = useState(null);
  const [showWidget, setShowWidget]           = useState(false);
  const [radiusKm, setRadiusKm]               = useState(5);

  const getUserLocation = () => {
    navigator.geolocation?.getCurrentPosition(
      (p) => setUserLocation({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => setUserLocation({ lat: 19.4326, lng: -99.1332 })
    );
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchRestaurants = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const r = await fetch(`${API_URL}/api/clients/restaurants?${params}`, { headers: authHeaders() });
      if (r.ok) setRestaurants(await r.json());
    } catch {}
  }, [search]);

  useEffect(() => { if (isOpen) { fetchRestaurants(); getUserLocation(); } }, [isOpen, fetchRestaurants]);

  const openGoogleMaps = (r) => {
    if (r?.coordinates) window.open(`https://www.google.com/maps/dir/?api=1&destination=${r.coordinates.lat},${r.coordinates.lng}`, '_blank');
    else if (r?.address) window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(r.address)}`, '_blank');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center">
      <div className="w-full max-w-lg bg-white rounded-t-3xl sm:rounded-3xl max-h-[95vh] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-slate-100 flex-shrink-0">
          <h2 className="font-bold text-slate-900">Nueva Reservación</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl transition-colors"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" placeholder="Buscar restaurante..." value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">Radio:</span>
            {[3,5,10,15].map(km => (
              <button key={km} onClick={() => setRadiusKm(km)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${radiusKm === km ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                {km}km
              </button>
            ))}
          </div>
          <div className="relative w-full h-64 bg-slate-100 rounded-2xl overflow-hidden" style={{ isolation: 'isolate', zIndex: 0 }}>
            {userLocation ? (
              <RestaurantMap restaurants={restaurants} userLocation={userLocation} selectedRestaurant={selectedRestaurant}
                onSelectRestaurant={setSelectedRestaurant} radiusKm={radiusKm} onGetDirections={openGoogleMaps} showRadius />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-center">
                  <LocateFixed className="w-7 h-7 text-slate-400 mx-auto mb-2 animate-pulse" />
                  <p className="text-slate-400 text-sm">Obteniendo ubicación...</p>
                </div>
              </div>
            )}
          </div>
          <h3 className="font-semibold text-slate-700">Restaurantes cercanos</h3>
          <div className="space-y-3">
            {restaurants.map(r => (
              <div key={r.id} onClick={() => setSelectedRestaurant(r)}
                className={`p-4 rounded-2xl border cursor-pointer transition-all ${selectedRestaurant?.id === r.id ? 'border-orange-500 bg-orange-50' : 'border-slate-100 bg-white hover:border-orange-200'}`}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-slate-100 overflow-hidden flex-shrink-0">
                    {r.logo_url ? <img src={r.logo_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Utensils className="w-6 h-6 text-slate-400" /></div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-slate-900">{r.name}</h4>
                    <p className="text-slate-400 text-sm truncate">{r.address}</p>
                  </div>
                </div>
                {selectedRestaurant?.id === r.id && (
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <button onClick={e => { e.stopPropagation(); setShowWidget(true); }}
                      className="py-2 px-3 rounded-xl bg-orange-500 text-white font-medium flex items-center justify-center gap-2 hover:bg-orange-600 text-sm">
                      <Calendar className="w-4 h-4" /> Reservar
                    </button>
                    <button onClick={e => { e.stopPropagation(); openGoogleMaps(r); }}
                      className="py-2 px-3 rounded-xl bg-slate-100 text-slate-700 font-medium flex items-center justify-center gap-2 hover:bg-slate-200 text-sm">
                      <Navigation className="w-4 h-4" /> Cómo llegar
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        {showWidget && selectedRestaurant && (
          <ReservationWidgetModal restaurant={selectedRestaurant} token={token}
            onClose={() => setShowWidget(false)} onSuccess={() => { setShowWidget(false); onSuccess(); }} />
        )}
      </div>
    </div>
  );
}

// ==================== RESERVATION WIDGET MODAL ====================
function ReservationWidgetModal({ restaurant, token, onClose, onSuccess }) {
  const [formData, setFormData] = useState({ date: new Date().toISOString().split('T')[0], time: '', party_size: 2, special_requests: '' });
  const [timeSlots, setTimeSlots] = useState([]);
  const [loading, setLoading]     = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/clients/restaurants/${restaurant.id}/availability?date=${formData.date}`, { headers: authHeaders() });
        if (r.ok) { const d = await r.json(); setTimeSlots(d.time_slots || []); }
      } catch {}
    })();
  }, [formData.date, restaurant.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.time) { toast.error('Selecciona una hora'); return; }
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/clients/my/reservations`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ restaurant_id: restaurant.id, ...formData })
      });
      if (!r.ok) throw new Error('Error al crear reservación');
      toast.success('¡Reservación creada!');
      onSuccess();
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center"><Utensils className="w-5 h-5 text-orange-500" /></div>
            <h3 className="font-bold text-slate-900">{restaurant.name}</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Fecha</label>
            <input type="date" value={formData.date} min={new Date().toISOString().split('T')[0]}
              onChange={e => setFormData({ ...formData, date: e.target.value })}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Hora disponible</label>
            <div className="grid grid-cols-4 gap-2">
              {timeSlots.length === 0 && <p className="col-span-4 text-slate-400 text-sm">Sin horarios disponibles</p>}
              {timeSlots.map(s => (
                <button key={s.id} type="button" onClick={() => setFormData({ ...formData, time: s.start_time })}
                  className={`py-2 px-2 rounded-xl text-sm font-medium transition-colors ${formData.time === s.start_time ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
                  {s.start_time}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Personas</label>
            <div className="flex items-center gap-4">
              <button type="button" onClick={() => setFormData({ ...formData, party_size: Math.max(1, formData.party_size - 1) })}
                className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 font-bold text-lg">−</button>
              <span className="text-xl font-bold text-slate-900 w-8 text-center">{formData.party_size}</span>
              <button type="button" onClick={() => setFormData({ ...formData, party_size: Math.min(20, formData.party_size + 1) })}
                className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 font-bold text-lg">+</button>
            </div>
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Notas especiales</label>
            <textarea value={formData.special_requests} onChange={e => setFormData({ ...formData, special_requests: e.target.value })}
              placeholder="Cumpleaños, alergias, silla de bebé..." rows={2}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none" />
          </div>
          <button type="submit" disabled={loading || !formData.time}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold disabled:opacity-50 hover:from-orange-600 hover:to-orange-700 transition-all shadow-md shadow-orange-500/30">
            {loading ? 'Creando...' : 'Confirmar Reservación'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ==================== SHARE / GIFT MODAL ====================
function ShareGiftModal({ isOpen, onClose, type, reservation, token, onSuccess }) {
  const [search, setSearch]             = useState('');
  const [users, setUsers]               = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [message, setMessage]           = useState('');
  const [giftImage, setGiftImage]       = useState(null);
  const [loading, setLoading]           = useState(false);
  const fileRef                         = useRef(null);

  useEffect(() => {
    if (isOpen && search.length >= 2) {
      fetch(`${API_URL}/api/clients/search/users?q=${encodeURIComponent(search)}`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : []).then(setUsers).catch(() => {});
    } else setUsers([]);
  }, [search, isOpen]);

  const handleClose = () => { setSearch(''); setUsers([]); setSelectedUser(null); setMessage(''); setGiftImage(null); onClose(); };

  const handleSubmit = async () => {
    if (!selectedUser) { toast.error('Selecciona un usuario'); return; }
    setLoading(true);
    try {
      const endpoint = type === 'share'
        ? `${API_URL}/api/clients/my/reservations/${reservation.id}/share`
        : `${API_URL}/api/clients/my/reservations/${reservation.id}/gift`;
      const r = await fetch(endpoint, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ recipient_client_id: selectedUser.id, message: message || null, gift_image: type === 'gift' ? giftImage : null })
      });
      if (!r.ok) throw new Error('Error al procesar');
      onSuccess();
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  if (!isOpen) return null;
  const isShare = type === 'share';

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center">
      <div className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            {isShare ? <Share2 className="w-5 h-5 text-blue-500" /> : <Gift className="w-5 h-5 text-purple-500" />}
            <h2 className="font-bold text-slate-900">{isShare ? 'Compartir' : 'Regalar'} Reservación</h2>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-slate-100 rounded-xl"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div className={`p-3 rounded-xl ${isShare ? 'bg-blue-50' : 'bg-purple-50'}`}>
            <p className={`text-sm ${isShare ? 'text-blue-600' : 'text-purple-600'}`}>
              {isShare ? 'El usuario podrá ver los detalles de tu reservación' : 'La reservación será transferida al usuario seleccionado'}
            </p>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" placeholder="Buscar por nombre o @nickname" value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </div>
          {users.length > 0 && (
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {users.map(u => (
                <button key={u.id} onClick={() => setSelectedUser(u)}
                  className={`w-full p-3 rounded-xl flex items-center gap-3 transition-all text-left ${selectedUser?.id === u.id ? isShare ? 'bg-blue-50 border-2 border-blue-400' : 'bg-purple-50 border-2 border-purple-400' : 'bg-slate-50 hover:bg-slate-100'}`}>
                  <div className="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold text-sm">{u.name?.charAt(0).toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 text-sm">{u.name}</p>
                    <p className="text-slate-400 text-xs">@{u.nickname}</p>
                  </div>
                  {selectedUser?.id === u.id && <Check className="w-4 h-4 text-emerald-500" />}
                </button>
              ))}
            </div>
          )}
          {selectedUser && (
            <textarea value={message} onChange={e => setMessage(e.target.value)}
              placeholder={isShare ? '¿Nos vemos ahí?' : '¡Disfrútalo!'} rows={2}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none" />
          )}
          {type === 'gift' && selectedUser && (
            <div>
              <input type="file" ref={fileRef} onChange={e => { const f = e.target.files[0]; if (f) { const r = new FileReader(); r.onloadend = () => setGiftImage(r.result); r.readAsDataURL(f); }}} accept="image/*" className="hidden" />
              {giftImage ? (
                <div className="relative">
                  <img src={giftImage} alt="Gift" className="w-full h-28 object-cover rounded-xl" />
                  <button onClick={() => setGiftImage(null)} className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"><X className="w-3.5 h-3.5" /></button>
                </div>
              ) : (
                <button onClick={() => fileRef.current?.click()} className="w-full py-6 border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center gap-2 hover:border-orange-400 transition-colors">
                  <Upload className="w-7 h-7 text-slate-300" />
                  <span className="text-slate-400 text-sm">Subir imagen (opcional)</span>
                </button>
              )}
            </div>
          )}
          <button onClick={handleSubmit} disabled={loading || !selectedUser}
            className={`w-full py-3 rounded-xl font-bold transition-all disabled:opacity-50 ${isShare ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-purple-500 text-white hover:bg-purple-600'}`}>
            {loading ? 'Procesando...' : isShare ? 'Compartir' : 'Regalar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================== MANAGE RESERVATION MODAL ====================
function ManageReservationModal({ isOpen, onClose, reservation, token, onSuccess }) {
  const [formData, setFormData] = useState({ date: '', time: '', party_size: 2, special_requests: '' });
  const [loading, setLoading]   = useState(false);

  useEffect(() => {
    if (reservation) setFormData({ date: reservation.date, time: reservation.time, party_size: reservation.party_size, special_requests: reservation.special_requests || '' });
  }, [reservation]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/clients/my/reservations/${reservation.id}`, {
        method: 'PATCH', headers: authHeaders(), body: JSON.stringify(formData)
      });
      if (!r.ok) throw new Error('Error al actualizar');
      toast.success('Reservación actualizada'); onSuccess();
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  if (!isOpen || !reservation) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-bold text-slate-900">Gestionar Reservación</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Fecha</label>
            <input type="date" value={formData.date} min={new Date().toISOString().split('T')[0]}
              onChange={e => setFormData({ ...formData, date: e.target.value })}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Hora</label>
            <input type="time" value={formData.time} onChange={e => setFormData({ ...formData, time: e.target.value })}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Personas</label>
            <div className="flex items-center gap-4">
              <button type="button" onClick={() => setFormData({ ...formData, party_size: Math.max(1, formData.party_size - 1) })} className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 font-bold text-lg">−</button>
              <span className="text-xl font-bold text-slate-900 w-8 text-center">{formData.party_size}</span>
              <button type="button" onClick={() => setFormData({ ...formData, party_size: Math.min(20, formData.party_size + 1) })} className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 font-bold text-lg">+</button>
            </div>
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Notas especiales</label>
            <textarea value={formData.special_requests} onChange={e => setFormData({ ...formData, special_requests: e.target.value })} rows={2}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none" />
          </div>
          <button type="submit" disabled={loading}
            className="w-full py-3 rounded-xl bg-orange-500 text-white font-bold hover:bg-orange-600 disabled:opacity-50 transition-all">
            {loading ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ==================== DIRECTIONS MODAL ====================
function DirectionsModal({ isOpen, onClose, reservation }) {
  if (!isOpen || !reservation) return null;
  const restaurant  = reservation.restaurant || {};
  const coordinates = restaurant.coordinates || { lat: 19.4326, lng: -99.1332 };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-bold text-slate-900">Cómo llegar a {restaurant.name}</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="w-full h-64">
          <iframe width="100%" height="100%" frameBorder="0" scrolling="no"
            src={`https://www.openstreetmap.org/export/embed.html?bbox=${coordinates.lng-0.01},${coordinates.lat-0.01},${coordinates.lng+0.01},${coordinates.lat+0.01}&layer=mapnik&marker=${coordinates.lat},${coordinates.lng}`}
            style={{ border: 0 }} />
        </div>
        <div className="p-4">
          <p className="text-slate-500 text-sm mb-3">{restaurant.address}</p>
          <button onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${coordinates.lat},${coordinates.lng}`, '_blank')}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold flex items-center justify-center gap-2 hover:from-orange-600 hover:to-orange-700">
            <Navigation className="w-5 h-5" /> Llévame ahí
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================== DIRECT MESSAGE MODAL ====================
function DirectMessageModal({ isOpen, onClose, reservation, token, onSuccess }) {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/clients/my/messages`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ restaurant_id: reservation.restaurant_id, reservation_id: reservation.id, message })
      });
      if (!r.ok) throw new Error('Error al enviar mensaje');
      setMessage(''); onSuccess();
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };
  if (!isOpen || !reservation) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-bold text-slate-900">Mensaje a {reservation.restaurant?.name}</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4">
          <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Escribe tu mensaje..." rows={4}
            className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none mb-4" />
          <button type="submit" disabled={loading || !message.trim()}
            className="w-full py-3 rounded-xl bg-blue-500 text-white font-bold hover:bg-blue-600 disabled:opacity-50 transition-all">
            {loading ? 'Enviando...' : 'Enviar Mensaje'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ==================== REBOOK MODAL ====================
function RebookModal({ isOpen, onClose, reservation, token, onSuccess }) {
  if (!isOpen || !reservation) return null;
  return <ReservationWidgetModal restaurant={reservation.restaurant} token={token} onClose={onClose} onSuccess={onSuccess} />;
}
